// Dữ liệu mẫu nhân sự (bản v486): Mint-HR · Mint-HR-OrgTree · Mint-LaborContract · Mint-HRDecision. KHÔNG lương, CCCD, số tài khoản.
// Nguồn thật về sau: TblStaff / TblDepartment / TblPosition qua DataSource (J-09).
import type {
  Concurrency, ContractStatus, ContractTemplate, DecisionStatus, DecisionType, DecisionTypeInfo, HandoverItem, HandoverKind, HrDecision, HrEvent,
  KpiScore, LaborContract, LeaveRequest, LeaveTone, OrgBlock, OrgSeat, SeatHold, SeatKind, Staff, StaffStatus, TimeCode, TimeRow, VacantSeat,
} from './types';

const seat = (code: string, kind: SeatKind = 'Primary'): SeatHold => ({ code, kind });
const staff = (id: string, name: string, seats: SeatHold[], dept: string, status: StaffStatus, joined: string, backup: string, leaveLeft: number): Staff => ({
  id, name, seats, dept, status, joined, backup, leaveLeft,
});

export const STAFF: readonly Staff[] = [
  staff('DAVID', 'Nguyễn Trường Giang', [seat('SALE-MGR'), seat('BOD-DDIR', 'Acting')], 'Kinh doanh', 'Đang làm', '02/Mar/2019', 'TUANNT · ANDG', 7.5),
  staff('PHILIP', 'Nguyễn Trường Sơn', [seat('BOD-DIR')], 'Ban giám đốc', 'Đang làm', '15/Jan/2016', '—', 10),
  staff('HANHLTM', 'Lê Thị Mỹ Hạnh', [seat('ACC-CP'), seat('HRD-MGR', 'Concurrent')], 'Kế toán · HC–NS', 'Đang làm', '11/Jun/2020', 'NGOCANH', 9),
  staff('PHONGNT', 'Nguyễn Thanh Phong', [seat('TRD-MGR'), seat('OPS-MGR', 'Concurrent')], 'Vận tải · Vận hành', 'Đang làm', '04/Apr/2018', 'THANHTTK', 6),
  staff('NhungTT', 'Thân Thị Nhung', [seat('ACC-TAX')], 'Kế toán', 'Đang làm', '09/Sep/2021', 'LIENNTK', 8),
  staff('MAITTN', 'Trịnh Thị Ngọc Mai', [seat('CSD-CSO')], 'Dịch vụ KH', 'Nghỉ phép', '20/Feb/2022', 'ThuyNTT', 4.5),
  staff('TUANNT', 'Nguyễn Thanh Tuấn', [seat('SALE-SE')], 'Kinh doanh', 'Nghỉ phép', '01/Aug/2023', 'ANDG', 3),
  staff('PhuongHT', 'Hà Thanh Phương', [seat('CSD-BKG')], 'Dịch vụ KH', 'Đang làm', '12/Oct/2021', 'TranTPB', 9.5),
  staff('NhiTHT', 'Trần Hoàng Thảo Nhi', [seat('CSD-BKG')], 'Dịch vụ KH', 'Đang làm', '05/May/2022', 'PhuongHT', 11),
  staff('THANHTTK', 'Trương Thị Kim Thành', [seat('TRD-DSP')], 'Vận tải', 'Đang làm', '18/Jul/2024', 'PHONGNT', 5),
  staff('LAN', 'Võ Thị Lan', [seat('CSD-CSO')], 'Dịch vụ KH', 'Thử việc', '03/Nov/2026', 'ThuyNTT', 0),
  staff('BẢO', 'Trần Quốc Bảo', [seat('SALE-SE')], 'Kinh doanh', 'Thử việc', '20/Oct/2026', 'TUANNT', 0),
];

const os = (code: string, name: string, plan: number, holders: Array<[string, SeatKind?]>): OrgSeat => ({
  code, name, plan, holders: holders.map(([id, kind]) => seat(id, kind)),
});

/** Cây tổ chức: khối → phòng → ghế → người giữ (board Mint-HR tab Cơ cấu & ghế, Mint-HR-OrgTree). */
export const ORG: readonly OrgBlock[] = [
  { name: 'Khối Kinh doanh', depts: [
    { name: 'Phòng Kinh doanh (SALE)', seats: [
      os('SALE-MGR', 'Trưởng phòng Kinh doanh', 1, [['DAVID']]),
      os('SALE-SE', 'Nhân viên Kinh doanh', 3, [['TUANNT'], ['ANDG'], ['BẢO']]),
      os('SALE-PRC', 'Nhân viên Pricing', 1, [['TRANGHDD']]),
    ] },
    { name: 'Phòng Dịch vụ khách hàng (CSD)', seats: [
      os('CSD-MGR', 'Trưởng phòng DVKH', 1, []),
      os('CSD-TL', 'Trưởng nhóm DVKH', 1, []),
      os('CSD-BKG', 'Nhân viên Đặt chỗ', 4, [['PhuongHT'], ['NhiTHT'], ['TranTPB'], ['PHUONGNKT']]),
      os('CSD-CSO', 'Nhân viên DVKH', 8, [['MAITTN'], ['HOATTN'], ['ThuyNTT'], ['LAN']]),
    ] },
  ] },
  { name: 'Khối Vận hành', depts: [
    { name: 'Phòng Vận hành (OPS)', seats: [
      os('OPS-MGR', 'Trưởng phòng Vận hành', 1, [['PHONGNT', 'Concurrent']]),
      os('OPS-DOC', 'Nhân viên chứng từ', 3, [['HoaTU'], ['NGOCNTB'], ['NHI Đ.'], ['THUỲ V.']]),
      os('OPS-FLD', 'Nhân viên hiện trường', 3, [['ANH'], ['AnNV'], ['KietLT']]),
    ] },
    { name: 'Phòng Vận tải (TRD)', seats: [
      os('TRD-MGR', 'Trưởng phòng Vận tải', 1, [['PHONGNT']]),
      os('TRD-DSP', 'Điều độ', 1, [['THANHTTK']]),
      os('TRD-DRV', 'Tài xế', 10, [['8 tài xế']]),
      os('TRD-TEC', 'Cố vấn kỹ thuật', 1, []),
    ] },
  ] },
  { name: 'Khối Hỗ trợ', depts: [
    { name: 'Kế toán (ACC)', seats: [
      os('ACC-CA', 'Kế toán trưởng', 1, []),
      os('ACC-CP', 'Kế toán chi phí', 1, [['HANHLTM']]),
      os('ACC-AR', 'Kế toán công nợ', 1, [['LIENNTK']]),
      os('ACC-TAX', 'Kế toán thuế', 1, [['NhungTT']]),
    ] },
    { name: 'HC–NS (HRD)', seats: [
      os('HRD-MGR', 'Trưởng phòng HC–NS', 1, [['HANHLTM', 'Concurrent']]),
      os('HRD-IT', 'Nhân viên CNTT', 1, []),
    ] },
    { name: 'Ban giám đốc (BOD)', seats: [
      os('BOD-DIR', 'Giám đốc', 1, [['PHILIP']]),
      os('BOD-DDIR', 'Phó Giám đốc', 1, [['DAVID', 'Acting']]),
    ] },
  ] },
];

export const CONCURRENCIES: readonly Concurrency[] = [
  { who: 'DAVID', seats: 'SALE-MGR (chính) + BOD-DDIR', kind: 'Quyền', decision: 'QĐ BOD-2026-014 · từ 01/Sep/2026', allowance: 'có phụ cấp' },
  { who: 'HANHLTM', seats: 'ACC-CP (chính) + HRD-MGR', kind: 'Kiêm', decision: 'QĐ HR-KIEMNHIEM-2026-002 · từ 05/Oct/2026', allowance: 'có phụ cấp' },
  { who: 'PHONGNT', seats: 'TRD-MGR (chính) + OPS-MGR', kind: 'Kiêm', decision: 'QĐ HR-KIEMNHIEM-2026-001 · từ 01/Jul/2026', allowance: 'có phụ cấp' },
];

export const VACANT_SEATS: readonly VacantSeat[] = [
  { code: 'ACC-CA', name: 'Kế toán trưởng', escalateTo: '[BOD-DIR] PHILIP', proposed: 'NhungTT', authorities: ['CREDIT-OVERRIDE', 'PAYMENT-APPROVE', 'WRITE-OFF'] },
  { code: 'CSD-MGR', name: 'Trưởng phòng DVKH', escalateTo: '[BOD-DIR] PHILIP', proposed: 'MAITTN', authorities: ['DDL', 'ISR'] },
  { code: 'CSD-TL', name: 'Trưởng nhóm DVKH', escalateTo: '[CSD-MGR] trống → [BOD-DIR] PHILIP', proposed: 'MAITTN', authorities: [] },
  { code: 'HRD-IT', name: 'Nhân viên CNTT', escalateTo: '[HRD-MGR] HANHLTM (kiêm)', proposed: 'DAVID', authorities: [] },
];

const tr = (who: string, seatCode: string, inn: string, out: string, site: string, note: string, code: TimeCode, needsProof?: boolean): TimeRow => ({
  who, seat: seatCode, in: inn, out, site, note, code, ...(needsProof ? { needsProof } : {}),
});

/** Chấm công "Hôm nay · 22/Oct". */
export const TIME_ROWS: readonly TimeRow[] = [
  tr('DAVID', 'SALE-MGR', '07:52', '—', 'Văn phòng 235 Cộng Hòa', '', 'W'),
  tr('MAITTN', 'CSD-CSO', '—', '—', '—', 'Nghỉ đột xuất · đã báo vắng 07:31', 'LV'),
  tr('PhuongHT', 'CSD-BKG', '08:14', '—', 'Văn phòng 235 Cộng Hòa', 'Trễ 14 phút', 'L'),
  tr('ANH', 'OPS-FLD', '07:20', '—', 'Cảng Cát Lái', '', 'W'),
  tr('KietLT', 'OPS-FLD', '07:45', '—', 'Ngoài geofence · KCN Sóng Thần', 'Công tác: giao chứng từ gốc khách RUITONGVN-SC', 'E', true),
  tr('ANDG', 'SALE-SE', '09:05', '—', 'Check-in thăm khách · GREENBUD', 'Sale đi khách', 'E'),
  tr('THANHTTK', 'TRD-DSP', '07:58', '—', 'Văn phòng 235 Cộng Hòa', 'Trực tối 21/Oct · OT 2,5 h', 'OT'),
  tr('HANHLTM', 'ACC-CP', '08:03', '—', 'Văn phòng 235 Cộng Hòa', '', 'W'),
  tr('TUANNT', 'SALE-SE', '—', '—', '—', 'Phép năm 22–23/Oct', 'LV'),
];

const lv = (owner: string, no: string, type: string, range: string, days: number, route: string, status: string, tone: LeaveTone, handover: string, note: string): LeaveRequest => ({
  owner, no, type, range, days, route, status, tone, handover, note,
});

export const LEAVES: readonly LeaveRequest[] = [
  lv('PhuongHT', 'LV-2610-021', 'Phép năm', '03–05/Nov/2026', 3, '[SALE-MGR] DAVID → [BOD-DIR] PHILIP', 'Chờ TBP', 'warn', 'TranTPB', 'Từ 31/Oct 08:00 (ngày làm việc trước kỳ nghỉ)'),
  lv('MAITTN', 'LV-2610-020', 'Nghỉ đột xuất', '22/Oct/2026', 1, 'Báo vắng 07:31 · bổ sung đơn trong 2 ngày', 'Đang hiệu lực', 'pri', 'ThuyNTT', 'Có hiệu lực ngay 07:31'),
  lv('TUANNT', 'LV-2610-018', 'Phép năm', '22–23/Oct/2026', 2, '[SALE-MGR] DAVID → [BOD-DIR] PHILIP', 'Đang nghỉ', 'ok', 'ANDG', 'Từ 21/Oct 08:00'),
  lv('THANHTTK', 'LV-2610-017', 'Việc cá nhân', '28/Oct/2026 · buổi sáng', 0.5, '[TRD-MGR] PHONGNT', 'Chờ duyệt', 'warn', 'PHONGNT', 'Chỉ buổi sáng'),
  lv('HANHLTM', 'LV-2610-015', 'Ốm đau', '15–16/Oct/2026', 2, '[BOD-DIR] PHILIP (TBP nghỉ → 1 cấp BOD)', 'Chờ hoàn trả', 'warn', 'NGOCANH', 'Đã hết hạn'),
  lv('NhiTHT', 'LV-2610-011', 'Cưới hỏi', '09–11/Oct/2026', 3, '[SALE-MGR] DAVID → [BOD-DIR] PHILIP', 'Đã đóng', 'mute', 'PhuongHT', 'Đã hoàn trả 14/Oct'),
];

const ho = (kind: HandoverKind, label: string, obj: string, note: string): HandoverItem => ({ kind, label, obj, note });

/** Bàn giao 7 việc trong đơn nghỉ LV-2610-021 của PhuongHT → TranTPB. */
export const HANDOVER: readonly HandoverItem[] = [
  ho('job', 'JOB', 'SE260639142 · RUITONGVN-SC', 'BookingTeam · Standby, cut-off 24/Oct 11:00'),
  ho('job', 'JOB', 'SE260639139 · GREENBUD', 'BookingTeam · Amend chờ hãng ONE'),
  ho('task', 'TASK', 'BKO · mở book MSC RAYA 2243W', 'Hạn 03/Nov 08:00'),
  ho('task', 'TASK', 'RQU · RQ Update ARRIVE', 'Hạn 03/Nov 10:00'),
  ho('svc', 'DV', 'SCY · đóng bãi Cát Lái 2×20DC', 'Bước 2/4 · hạn 04/Nov'),
  ho('job', 'JOB', 'SE260639127 · NDP', 'Stock booking · hạn trả chỗ 04/Nov'),
  ho('trf', 'TRF', 'TRF2610081 · COCOGROUP', 'Vận tải · Finish chuyến Bình Dương → Cát Lái trước 04/Nov'),
];

const kp = (who: string, seatCode: string, share: number, items: string, score: number): KpiScore => ({ who, seat: seatCode, share, items, score });

export const KPI_SCORES: readonly KpiScore[] = [
  kp('DAVID', 'SALE-MGR', 100, 'GP đã thu · khách mới · nợ quá hạn', 104),
  kp('HANHLTM', 'ACC-CP', 60, 'HĐ ĐỐI TÁC khớp · khoá kỳ đúng ngày', 98),
  kp('HANHLTM', 'HRD-MGR · kiêm', 40, 'Chốt công đúng hạn · tuyển đủ định biên', 86),
  kp('PHONGNT', 'TRD-MGR', 100, 'Chuyến đúng giờ · chi phí xe/km (ghế kiêm không chấm)', 101),
  kp('PhuongHT', 'CSD-BKG', 100, 'Booking đủ thông tin lần đầu · tỷ lệ dùng chỗ stock', 97),
  kp('MAITTN', 'CSD-CSO', 100, 'Cut-off không trễ · B/L không sửa do lỗi nội bộ', 92),
  kp('THANHTTK', 'TRD-DSP', 100, 'Gán xe trước 16:00 · nhận sự cố ≤ 15 phút', 95),
  kp('ANDG', 'SALE-SE', 100, 'GP đã thu · báo giá đúng SLA', 88),
];

const ev = (kind: string, who: string, sub: string, due: string, tone: HrEvent['tone'], to: HrEvent['to'], action: string): HrEvent => ({ kind, who, sub, due, tone, to, action });

/** Sự kiện sắp tới · 30 ngày (Tổng quan). */
export const EVENTS: readonly HrEvent[] = [
  ev('Hết thử việc', 'LAN · Võ Thị Lan · CSD-CSO', 'TBP đánh giá → lập HĐ chính thức từ mẫu', '31/Oct/2026', 'warn', 'contract', 'Lập HĐ'),
  ev('Hết HĐ', 'MAITTN · HĐ 12 tháng', 'Gia hạn hoặc chuyển HĐ không thời hạn', '19/Nov/2026', 'warn', 'contract', 'Gia hạn HĐ'),
  ev('Hết HĐ', 'THANHTTK · HĐ 12 tháng', 'Gia hạn HĐ · TBP [TRD-MGR] PHONGNT xác nhận', '17/Nov/2026', 'warn', 'contract', 'Gia hạn HĐ'),
  ev('Hết thử việc', 'BẢO · Trần Quốc Bảo · SALE-SE', 'QĐ tiếp nhận chính thức', '20/Dec/2026', 'pri', 'decision', 'Lập QĐ'),
  ev('Kiêm nhiệm', 'HANHLTM · HRD-MGR', 'QĐ kiêm nhiệm hết hiệu lực → lập QĐ mới hoặc thôi kiêm', '05/Jan/2027', 'pri', 'decision', 'Lập QĐ'),
  ev('Chứng chỉ', 'PHONGNT · ATVSLĐ', 'Gia hạn chứng chỉ an toàn lao động', '21/Nov/2026', 'mute', 'training', 'Mở đào tạo'),
  ev('Sinh nhật', '2 người trong tuần', 'Gửi lời chúc qua myHANA · không hiện ngày sinh', 'tuần này', 'ok', '', ''),
];

const tpl = (code: string, name: string, kind: ContractTemplate['kind'], term: string, law: string): ContractTemplate => ({ code, name, kind, term, version: 'v1.0', law });

export const CONTRACT_TEMPLATES: readonly ContractTemplate[] = [
  tpl('HDTV-02M', 'HĐ thử việc 2 tháng', 'ThuViec', '2 tháng', 'BLLĐ 2019 Điều 24–27'),
  tpl('HDDT-06M', 'HĐ đào tạo nghề 6 tháng', 'DaoTaoNghe', '6 tháng', 'BLLĐ 2019 Điều 61–62'),
  tpl('HDLD-12M', 'HĐLĐ xác định thời hạn 12 tháng', 'XacDinhThoiHan', '12 tháng', 'BLLĐ 2019 Điều 20'),
  tpl('HDLD-36M', 'HĐLĐ xác định thời hạn 36 tháng', 'XacDinhThoiHan', '36 tháng', 'BLLĐ 2019 Điều 20'),
  tpl('HDLD-KXD', 'HĐLĐ không xác định thời hạn', 'KhongXacDinhThoiHan', '—', 'BLLĐ 2019 Điều 20'),
];

const lc = (no: string, user: string, name: string, seatCode: string, template: string, kindLabel: string, nth: number, from: string, to: string, status: ContractStatus, daysLeft: number | null): LaborContract => ({
  no, user, name, seat: seatCode, template, kindLabel, nth, from, to, status, daysLeft,
});

export const CONTRACTS: readonly LaborContract[] = [
  lc('HN-HDTV-2026-007', 'LanVT', 'Võ Thị Lan (mẫu)', 'CSD-CSO', 'HDTV-02M-v1.0', 'Thử việc', 1, '03/Nov/2026', '02/Jan/2027', 'Draft', null),
  lc('HN-HDTV-2026-006', 'BaoTQ', 'Trần Quốc Bảo (mẫu)', 'SALE-SE', 'HDTV-02M-v0.9', 'Thử việc', 1, '20/Oct/2026', '19/Dec/2026', 'Signed', 58),
  lc('HN-HDLD-12M-2025-030', 'AnNV', 'Nguyễn Văn An', 'OPS-FLD', 'HDLD-12M-v0.9', 'XĐTH 12 tháng', 1, '10/Nov/2025', '09/Nov/2026', 'Signed', 18),
  lc('HN-HDLD-12M-2025-017', 'NhiTHT', 'Trần Hoàng Thảo Nhi', 'CSD-BKG', 'HDLD-12M-v0.9', 'XĐTH 12 tháng', 1, '16/Nov/2025', '15/Nov/2026', 'Signed', 24),
  lc('HN-HDLD-12M-2025-022', 'THANHTTK', 'Trương Thị Kim Thành', 'TRD-DSP', 'HDLD-12M-v0.9', 'XĐTH 12 tháng', 2, '01/Dec/2025', '30/Nov/2026', 'Signed', 39),
  lc('HN-HDLD-12M-2025-014', 'PhuongHT', 'Hà Thanh Phương', 'CSD-BKG', 'HDLD-12M-v0.9', 'XĐTH 12 tháng', 2, '12/Jan/2026', '11/Jan/2027', 'Signed', 81),
  lc('HN-HDLD-36M-2025-009', 'KHAINH', 'KHAINH', 'TRD-DRV', 'HDLD-36M-v0.9', 'XĐTH 36 tháng', 1, '05/Mar/2025', '04/Mar/2028', 'Signed', 864),
  lc('HN-HDLD-KXD-2023-003', 'HANHLTM', 'Lê Thị Mỹ Hạnh', 'ACC-CP', 'HDLD-KXD-v0.9', 'Không xác định', 3, '01/Jul/2023', '—', 'Signed', null),
  lc('HN-HDLD-12M-2024-011', 'QuangPV', 'Phạm Văn Quang (mẫu)', 'TRD-DRV', 'HDLD-12M-v0.9', 'XĐTH 12 tháng', 1, '01/Nov/2024', '31/Oct/2026', 'Terminated', null),
];

const ty = (type: DecisionType, label: string, template: string, money: boolean): DecisionTypeInfo => ({ type, label, template, money });

export const DECISION_TYPES: readonly DecisionTypeInfo[] = [
  ty('TangLuong', 'Tăng / điều chỉnh lương', 'QD-TANGLUONG', true),
  ty('ThangCap', 'Thăng cấp', 'QD-THANGCAP', false),
  ty('DieuChuyen', 'Điều chuyển', 'QD-DIEUCHUYEN', false),
  ty('KiemNhiem', 'Kiêm nhiệm', 'QD-KIEMNHIEM', false),
  ty('ThuMoi', 'Thư mời nhận việc', 'TB-THUMOI', true),
  ty('BoNhiem', 'Bổ nhiệm', 'QD-BONHIEM', false),
  ty('NghiViec', 'Nghỉ việc / khôi phục', 'chưa có mẫu', false),
  ty('DieuChinhKPI', 'Điều chỉnh KPI', 'chưa có mẫu', true),
];

const HRM = '[HRD-MGR · kiêm] HANHLTM';
const dc = (no: string, type: DecisionType, user: string, name: string, seatCode: string, before: string, after: string, effective: string, approver: string, status: DecisionStatus, createdAt: string, reason?: string): HrDecision => ({
  no, type, user, name, seat: seatCode, before, after, effective, approver, status, createdAt, ...(reason ? { reason } : {}),
});

// Loại có tiền (TangLuong · ThuMoi · DieuChinhKPI): nội dung trước / sau để trống — số thật thuộc màn có quyền lương (luồng K).
export const DECISIONS: readonly HrDecision[] = [
  dc('HR-TANGLUONG-2026-012', 'TangLuong', 'PhuongHT', 'Hà Thanh Phương', 'CSD-BKG · L03', '', '', '01/Nov/2026', HRM, 'Pending', '21/Oct/2026 09:10'),
  dc('HR-NGHIVIEC-2026-001', 'NghiViec', 'QuangPV', 'Phạm Văn Quang (mẫu)', 'TRD-DRV · L02', 'Đang làm · Enabled', 'Nghỉ việc từ 31/Oct/2026 · khoá M365 + HANA Ezy 17:30', '31/Oct/2026', HRM, 'Pending', '22/Oct/2026 07:50'),
  dc('HR-KIEMNHIEM-2026-004', 'KiemNhiem', 'NhungTT', 'Thân Thị Nhung', 'ACC-TAX', 'ACC-TAX (Primary)', '+ ACC-CA Kế toán trưởng · Acting · 24/Oct–31/Dec/2026', '24/Oct/2026', HRM, 'Pending', '22/Oct/2026 08:20'),
  dc('HR-THANGCAP-2026-004', 'ThangCap', 'NhiTHT', 'Trần Hoàng Thảo Nhi', 'CSD-BKG · L03', 'L03 · Executive', 'L04 · Junior · giữ ghế CSD-BKG', '01/Nov/2026', HRM, 'Pending', '20/Oct/2026 16:30'),
  dc('HR-DIEUCHUYEN-2026-002', 'DieuChuyen', 'AnNV', 'Nguyễn Văn An', 'OPS-FLD · L03', 'OPS · OPS-FLD · cấp trên [OPS-MGR · kiêm] PHONGNT', 'TRD · TRD-DSP · cấp trên [TRD-MGR] PHONGNT', '01/Nov/2026', HRM, 'Pending', '19/Oct/2026 11:05'),
  dc('TB-THUMOI-2026-006', 'ThuMoi', 'LanVT', 'Võ Thị Lan (ứng viên)', 'CSD-CSO · L02', '', '', '03/Nov/2026', HRM, 'Approved', '16/Oct/2026 10:00'),
  dc('HR-KIEMNHIEM-2026-002', 'KiemNhiem', 'HANHLTM', 'Lê Thị Mỹ Hạnh', 'ACC-CP · L05', 'ACC-CP (Primary)', '+ HRD-MGR · Concurrent · KPI 60/40', '05/Oct/2026', '[BOD-DDIR · quyền] DAVID', 'Approved', '01/Oct/2026 14:00'),
  dc('HR-BONHIEM-2026-001', 'BoNhiem', 'DAVID', 'Nguyễn Trường Giang', 'SALE-MGR · L07', 'SALE-MGR (Primary)', '+ BOD-DDIR Phó Giám đốc · Acting', '01/Sep/2026', HRM, 'Approved', '28/Aug/2026 09:00'),
  dc('HR-DIEUCHINHKPI-2026-003', 'DieuChinhKPI', 'DAVID', 'Nguyễn Trường Giang', 'SALE-MGR · L07', '', '', '01/Oct/2026', HRM, 'Rejected', '25/Sep/2026 15:20', 'Chờ chốt chỉ tiêu quý IV ở họp BOD 02/Oct'),
];
